import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { addressCountries, CaseDocketEntry, ReturnAddress, SignatureBlock } from '../../utils/constant';
import {
  addCertificate,
  addReturnAddressToDb,
  editCertificatesInDB,
  editReturnAddressInDb,
  fetchCaseInfo,
  formatCreditors,
  formatParties,
  getReturnAddresses,
  getUserCertificates,
  removeCertificate,
  removeReturnAddressInDb,
  sendDataForProcessing,
  processUserSuppliedAddress,
  checkAvailableServiceOptions,
} from './utils';
import { initialState } from './contansts';
import * as uuid from 'uuid';
import { formatCaseNumber, mapMultipleCaseNumbers } from 'src/utils/utility-functions';
import { isRecipientExcluded } from 'src/pages/create-notice/utilities/utils';
import { apiInstance } from '../../api/apiInstance';
import { DIRECT_MAILING_PERSONALIZED_ENABLED } from '../../utils/feature-flags';


// Lower-cased version for case-insensitive lookups (e.g. normalizedLines returned by the backend
// may not match the Title Case stored in addressCountries).
const addressCountriesSetLower = new Set(addressCountries.map((c) => c.toLowerCase()));

const uploadSlice = createSlice({
  name: 'upload',
  initialState,
  reducers: {
    setNoticeFlowType(state, action) {
      if (['legal', 'direct'].includes(action.payload)) {
        state.formData.caseSource.userSupplied = true;
      }

      // When the Personalized workflow flag is off, auto-set Non-Personalized so
      // all downstream consumers (navigation, validation, submission) have the
      // correct type without requiring the user to pass through Select Mailing Type.
      if (action.payload === 'direct' && !DIRECT_MAILING_PERSONALIZED_ENABLED) {
        state.formData.directMailingType = { type: 'direct_mail_non_personalized' };
      }

      state.noticeType = { type: action.payload };
    },
    updateFormData(state, action) {
      state.formData = {
        ...state.formData,
        ...action.payload,
      };
    },
    updateServedParties(state, action) {
      state.formData.edcaServedParty = {
        ...state.formData.edcaServedParty,
        ...action.payload,
      };
    },
    addUploadedPdfs(state, action) {
      state.formData = {
        ...state.formData,
        uploadedPdfs: [...state.formData.uploadedPdfs, ...action.payload],
      };
      state.focusedFile = action.payload[action.payload.length - 1];
    },
    addUploadedProofOfClaim(state, action) {
      state.formData = {
        ...state.formData,
        uploadedProofOfClaim: [...state.formData.uploadedProofOfClaim, ...action.payload],
      };
    },
    addUploadedBallot(state, action) {
      state.formData = {
        ...state.formData,
        uploadedBallot: [...state.formData.uploadedBallot, ...action.payload],
      };
    },
    updateUploadedPfs(state, action) {
      state.formData = {
        ...state.formData,
        uploadedPdfs: state.formData.uploadedPdfs.map((item) =>
          item.id === action.payload.id
            ? {
                ...item,
                [action.payload.type]: action.payload.value,
              }
            : item,
        ),
        uploadedBallot:
          state.formData.uploadedBallot[0]?.id === action.payload.id
            ? [
                {
                  ...state.formData.uploadedBallot[0],
                  [action.payload.type]: action.payload.value,
                },
              ]
            : state.formData.uploadedBallot,
        uploadedProofOfClaim:
          state.formData.uploadedProofOfClaim[0]?.id === action.payload.id
            ? [
                {
                  ...state.formData.uploadedProofOfClaim[0],
                  [action.payload.type]: action.payload.value,
                },
              ]
            : state.formData.uploadedProofOfClaim,
      };
    },
    updateUploadedBallot(state, action) {
      state.formData = {
        ...state.formData,
        uploadedBallot: {
          ...state.formData.uploadedBallot,
          [action.payload.type]: action.payload.value,
        },
      };
    },
    updateUploadedProofOfClaim(state, action) {
      state.formData = {
        ...state.formData,
        uploadedProofOfClaim: {
          ...state.formData.uploadedProofOfClaim,
          [action.payload.type]: action.payload.value,
        },
      };
    },
    reorderUploadedPdfs(state, action) {
      state.formData = {
        ...state.formData,
        uploadedPdfs: action.payload,
      };
    },
    removeUploadedPdf(state, action) {
      const deletedItem = state.formData.uploadedPdfs.find((item) => item.id === action.payload);
      if (!deletedItem) return;
      const newPdfs = state.formData.uploadedPdfs.filter((item) => item.id !== action.payload);
      state.formData = {
        ...state.formData,
        uploadedPdfs: newPdfs,
      };
      state.focusedFile =
        newPdfs.length > 0
          ? newPdfs[0]
          : state.formData.uploadedBallot.length > 0
            ? state.formData.uploadedBallot[0]
            : state.formData.uploadedProofOfClaim.length > 0
              ? state.formData.uploadedProofOfClaim[0]
              : null;
      state.totalUploadedPdfsFileSize -= deletedItem.baseSize;
      state.totalUploadedPdfsPageSize -= deletedItem.pageCount;
    },
    removeUploadedProofOfClaimPdf(state, action) {
      const deletedItem = state.formData.uploadedProofOfClaim.find((item) => item.id === action.payload);
      if (!deletedItem) return;
      state.formData = {
        ...state.formData,
        uploadedProofOfClaim: state.formData.uploadedProofOfClaim.filter((item) => item.id !== action.payload),
      };
      state.focusedFile =
        state.formData.uploadedPdfs.length > 0
          ? state.formData.uploadedPdfs[0]
          : state.formData.uploadedBallot.length > 0
            ? state.formData.uploadedBallot[0]
            : null;
      state.totalUploadedPdfsFileSize -= deletedItem.baseSize;
      state.totalUploadedPdfsPageSize -= deletedItem.pageCount;
    },
    removeUploadedBallotPdf(state, action) {
      const deletedItem = state.formData.uploadedBallot.find((item) => item.id === action.payload);
      if (!deletedItem) return;
      state.formData = {
        ...state.formData,
        uploadedBallot: state.formData.uploadedBallot.filter((item) => item.id !== action.payload),
      };
      state.focusedFile =
        state.formData.uploadedPdfs.length > 0
          ? state.formData.uploadedPdfs[0]
          : state.formData.uploadedProofOfClaim.length > 0
            ? state.formData.uploadedProofOfClaim[0]
            : null;
      state.totalUploadedPdfsFileSize -= deletedItem.baseSize;
      state.totalUploadedPdfsPageSize -= deletedItem.pageCount;
    },
    addPdfSizeToTotal(state, action) {
      state.totalUploadedPdfsFileSize += action.payload;
    },
    accumulateTotalPages(state, action) {
      state.totalUploadedPdfsPageSize = state.totalUploadedPdfsPageSize + action.payload;
    },
    addSelectedCourtDistrict(state, action) {
      state.courtDistricts = action.payload;
    },
    setFocusedFileInStore(state, action) {
      state.focusedFile = action.payload;
    },
    updateCasePartyStatus(state, action) {
      state.formData = {
        ...state.formData,
        caseParties: state.formData.caseParties.map((item, index) =>
          item.id === action.payload.name ? { ...item, [action.payload.type]: action.payload.value } : item,
        ),
      };
    },
    updateAttorneyStatus(state, action) {
      state.formData = {
        ...state.formData,
        attorneys: state.formData.attorneys.map((item, index) =>
          item.name === action.payload.name ? { ...item, [action.payload.type]: action.payload.value } : item,
        ),
      };
    },
    updateCreditorStatus(state, action) {
      state.formData = {
        ...state.formData,
        creditors: state.formData.creditors.map((item, index) =>
          item.id === action.payload.name ? { ...item, [action.payload.type]: action.payload.value } : item,
        ),
      };
    },
    updateUserAddedStatus(state, action) {
      const addressIndex = state.formData.userUploadedAddresses.findIndex((item) => item.id === action.payload.name);

      if (addressIndex !== -1) {
        state.formData = {
          ...state.formData,
          userUploadedAddresses: [
            ...state.formData.userUploadedAddresses.slice(0, addressIndex),
            {
              ...state.formData.userUploadedAddresses[addressIndex],
              [action.payload.type]: action.payload.value,
            },
            ...state.formData.userUploadedAddresses.slice(addressIndex + 1),
          ],
        };
      }
    },
    updateHearingInformation(state, action) {
      state.formData = {
        ...state.formData,
        certificatePreferences: {
          ...state.formData.certificatePreferences,
          hearingInformation: {
            ...state.formData.certificatePreferences.hearingInformation,
            [action.payload.name]: action.payload.value,
          },
        },
      };
    },
    updateCustomFileName(state, action) {
      state.formData = {
        ...state.formData,
        certificatePreferences: {
          ...state.formData.certificatePreferences,
          customFileName: action.payload.value,
        },
      };
    },
    updateCaseDocketEntriesMetaData(state, action) {
      state.formData = {
        ...state.formData,
        caseDocketEntries: state.formData.caseDocketEntries.map((item) =>
          item.id === action.payload.id
            ? {
                ...item,
                [action.payload.type]: action.payload.value,
              }
            : item,
        ),
      };
    },
    addCaseDocketIds(state, action) {
      state.formData = {
        ...state.formData,
        caseDocketEntries: state.formData.caseDocketEntries.map((item) =>
          item.id === action.payload.id
            ? {
                ...item,
                isChecked: action.payload.checked,
                docketDescription: !action.payload.checked ? '' : item.docketDescription,
              }
            : item,
        ),
      };
    },
    updateDebtor(state, action) {
      state.formData = {
        ...state.formData,
        debtors: state.formData.debtors.map((item) =>
          item.id === action.payload.id ? { ...item, [action.payload.type]: action.payload.value } : item,
        ),
      };
    },
    updateNewJersey(state, action) {
      const { id, relationToParty } = action.payload;

      // Update CaseParty
      state.formData.caseParties = state.formData.caseParties.map((item) =>
        item.id === id ? { ...item, newJerseyRelationshipOfParty: relationToParty } : item,
      );

      // Update Creditors
      state.formData.creditors = state.formData.creditors.map((item) =>
        item.id === id ? { ...item, newJerseyRelationshipOfParty: relationToParty } : item,
      );

      // Update AttorneyObj
      state.formData.attorneys = state.formData.attorneys.map((item) =>
        item.id === id ? { ...item, newJerseyRelationshipOfParty: relationToParty } : item,
      );

      state.formData.userUploadedAddresses = state.formData.userUploadedAddresses.map((item) =>
        item.id === id ? { ...item, newJerseyRelationshipOfParty: relationToParty } : item,
      );

      // Update formData if necessary
      state.formData = {
        ...state.formData,
        ...action.payload,
      };
    },
    removeCaseDocketIds(state, action) {
      state.formData = {
        ...state.formData,
        caseDocketEntries: state.formData.caseDocketEntries.filter(
          (item: CaseDocketEntry) => item.id !== action.payload.id,
        ),
      };
    },
    removeReturnAddress(state, action) {
      state.formData = {
        ...state.formData,
        returnAddresses: state.formData.returnAddresses.filter((item) => item.id !== action.payload),
      };
    },
    resetChapterOptions(state) {
      state.formData = {
        ...state.formData,
        subChapterV: '',
        proofOfClaim: '',
      };
    },
    loadUserSettings(state, action) {
      state.formData = {
        ...state.formData,
        [action.payload.type]: [action.payload.value],
      };
      state.hasLoadedUserSettings = true;
    },

    setErrors: (state, action) => {
      state.errors = action.payload;
    },
    setReturnErrors: (state, action) => {
      state.returnAddressErrors = action.payload;
    },
    removeReturnErrors: (state) => {
      state.returnAddressErrors = initialState.returnAddressErrors;
    },
    setSignatureErrors: (state, action) => {
      state.signatureBlocksErrors = action.payload;
    },
    removeSignatureErrors: (state) => {
      state.signatureBlocksErrors = initialState.signatureBlocksErrors;
    },
    setClerkErrors: (state, action) => {
      state.bankruptcyClerkErrors = action.payload;
    },
    clearErrors: (state) => {
      state.errors = { ...initialState.errors };
    },
    startNoticeCreation: (state) => {
      state.isCreatingNotice = { status: 'pending' };
    },
    cancelCreation: (state) => ({
      ...initialState,
      jobCreationResponse: state.jobCreationResponse,
    }),
    updateReturnEnvelope: (state, action) => {
      state.formData.returnEnvelope = {
        ...state.formData.returnEnvelope,
        [action.payload.type]: action.payload.value,
      };
    },
    resetReturnAddrStatus: (state) => {
      state.returnAddrStatus = null;
    },
    resetCertificateStatus: (state) => {
      state.certificateStatus = null;
    },
    resetCaseStatus: (state) => {
      state.caseInfoState = null;
    },
    resetErrors: (state) => {
      state.errors = initialState.errors;
    },
    updateCourtJurisdiction: (
      state,
      action: PayloadAction<{
        key: keyof { county: string; state: string; division: string };
        value: string;
      }>,
    ) => {
      const { key, value } = action.payload;
      state.formData.courtJurisdiction[key] = value;
    },
    updateLegalCaseFields: (
      state,
      action: PayloadAction<{
        key: keyof { plaintiff: string; defendant: string; estatePartyName: string };
        value: string;
      }>,
    ) => {
      const { key, value } = action.payload;

      state.formData.legalCaseFields[key] = value;
    },
    setCaseInfoError: (state, action) => {
      state.caseInfoState = {
        message: action.payload,
        status: 'error',
      };
    },
    clearUploadAddressesError: (state) => {
      state.uploadAddressesError = '';
    },
    addJointCaseNumber: (state) => {
      state.formData.jointCaseNumbers = [
        ...state.formData.jointCaseNumbers,
        { id: uuid.v4(), value: '', textfieldValue: '', debtorName: '' },
      ];
    },
    deleteJoinCaseNumber: (state, action) => {
      state.formData.jointCaseNumbers = state.formData.jointCaseNumbers.filter((item) => item.id !== action.payload);
    },
    updateJointCaseNumber: (state, action) => {
      const { id, value, textfieldValue } = action.payload;
      state.formData.jointCaseNumbers = state.formData.jointCaseNumbers.map((caseNumber) =>
        caseNumber.id === id ? { id, value, textfieldValue, debtorName: caseNumber.debtorName ?? '' } : caseNumber,
      );
    },
    updateJointCaseNumberDebtorName: (state, action) => {
      const { id, debtorName } = action.payload;
      state.formData.jointCaseNumbers = state.formData.jointCaseNumbers.map((caseNumber) =>
        caseNumber.id === id ? { ...caseNumber, debtorName } : caseNumber,
      );
    },
    clearSubmitJobError: (state) => {
      state.submitJobError = '';
    },
    setOnlyFetchingCaseParties: (state, action) => {
      state.fetchingOnlyCaseParties = action.payload;
    },
    setOnlyFetchingCreditors: (state, action) => {
      state.fetchingOnlyCreditors = action.payload;
    },
    setCasePartiesToCmEcfMailing: (state) => {
      state.formData.temporaryCasePartiesStatus = state.formData.caseParties;
      state.formData.caseParties = state.formData.caseParties.filter(party => !isRecipientExcluded(party)).map((party) => ({
        ...party,
        isCertified: false,
        isFirstClass: false,
        isOvernight: false,
        isCmEcf: true,
      }));
    },
    revertCasePartiesFromCmEcfToPreviousState: (state) => {
      state.formData.caseParties = state.formData.temporaryCasePartiesStatus.map((party) => {
        const currentParty = state.formData.caseParties.find((item) => item.id === party.id);
        return currentParty && !currentParty.isCmEcf ? currentParty : party;
      });
    },
    /**
     * Update the resolved `addressCountry` and `isValidAddress` on a single recipient after
     * POST /job/classify-addresses responds. Clears the fields when country/isValidAddress
     * are undefined (e.g. when address editing starts before re-classification).
     * Payload: { recipientType: 'caseParty' | 'creditor' | 'userUploaded', id: string, country?: string, normalizedLines?: string[], isValidAddress?: boolean }
     */
    setRecipientAddressCountry(
      state,
      action: { payload: { recipientType: 'caseParty' | 'creditor' | 'userUploaded'; id: string; country?: string; normalizedLines?: string[]; isValidAddress?: boolean } },
    ) {
      const { recipientType, id, country, normalizedLines, isValidAddress } = action.payload;
      if (recipientType === 'caseParty') {
        state.formData.caseParties = state.formData.caseParties.map((item) =>
          item.id === id
            ? { ...item, addressCountry: country, ...(normalizedLines?.length ? { address: normalizedLines } : {}) }
            : item,
        );
      } else if (recipientType === 'creditor') {
        state.formData.creditors = state.formData.creditors.map((item) =>
          item.id === id
            ? { ...item, addressCountry: country, ...(normalizedLines?.length ? { address: normalizedLines } : {}) }
            : item,
        );
      } else if (recipientType === 'userUploaded') {
        state.formData.userUploadedAddresses = state.formData.userUploadedAddresses.map((item) => {
          if (item.id !== id) return item;

          // Compute the post-classification address lines to decide whether isTooManyLines
          // should be cleared. Use normalizedLines if Bedrock returned them, otherwise
          // fall back to the current stored address.
          const resolvedAddress = normalizedLines?.length ? normalizedLines : item.address;
          const contentLineCount = resolvedAddress.filter(
            (l) => !addressCountriesSetLower.has(l.trim().toLowerCase())
          ).length;
          const resolvedCountryLine = resolvedAddress.find((l) => addressCountriesSetLower.has(l.trim().toLowerCase()));
          const resolvedIsNonUS = resolvedCountryLine !== undefined && resolvedCountryLine.trim().toLowerCase() !== 'united states';
          // For non-US addresses the country line occupies one printable slot,
          // so the effective content limit is 4. For US (no country line) it is 5.
          const resolvedEffectiveLimit = resolvedIsNonUS ? 4 : 5;
          // Only clear isTooManyLines when the line count is now within the limit.
          // This prevents the initial-upload classification from wiping the flag
          // before the user has had a chance to edit the address.
          const resolvedTooManyLines = contentLineCount > resolvedEffectiveLimit ? true : undefined;

          return {
            ...item,
            addressCountry: country,
            ...(normalizedLines?.length ? { address: normalizedLines } : {}),
            // Set isValidAddress from the classification result.
            // Clear isUserApproved if the address was re-classified (user edited it).
            isValidAddress: isValidAddress,
            isUserApproved: isValidAddress === true ? undefined : item.isUserApproved,
            // Preserve isTooManyLines if lines are still over the limit after classification.
            isTooManyLines: resolvedTooManyLines,
          };
        });
      }
    },
    /**
     * Mark a user-uploaded address as user-approved, overriding the invalid block.
     * Setting isUserApproved=true bypasses the navigation guard and removes the Invalid chip.
     * Payload: { id: string }
     */
    setRecipientUserApproved(
      state,
      action: { payload: { id: string } },
    ) {
      const { id } = action.payload;
      state.formData.userUploadedAddresses = state.formData.userUploadedAddresses.map((item) =>
        item.id === id ? { ...item, isUserApproved: true } : item,
      );
    },
    /**
     * Signal AddressPage to open the edit drawer for the given address id.
     * Set by BkNoticeFormButtons when the user clicks an address in the
     * invalid-addresses alert triggered from the Continue button.
     * Payload: string (address id) or null to clear.
     */
    setPendingEditAddressId(state, action: { payload: string | null }) {
      state.pendingEditAddressId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCaseInfo.pending, (state) => {
        state.caseInfoState = { message: '', status: 'pending' };
        state.hasFetchedCase = false;
      })
      .addCase(fetchCaseInfo.fulfilled, (state, action) => {
        state.caseInfoState = {
          message: '',
          status: 'success',
        };
        const requiredToChooseCaseNumber = Object.keys(action.payload.multipleCaseOptionsList ?? {})?.length > 0;
        state.requiredToChooseCaseNumber = requiredToChooseCaseNumber;

        if (state.fetchingOnlyCaseParties || state.fetchingOnlyCreditors) {
          state.formData = {
            ...state.formData,
            caseParties: state.fetchingOnlyCaseParties ? formatParties(action.payload) : state.formData.caseParties,
            creditors: state.fetchingOnlyCreditors ? formatCreditors(action.payload) : state.formData.creditors,
          };
          state.hasFetchedCase = true;
          state.fetchingOnlyCaseParties = false;
          return;
        }

        state.formData = {
          ...state.formData,
          caseNumber: {
            textfieldValue: formatCaseNumber(state.formData.caseNumber.textfieldValue),
            value: formatCaseNumber(state.formData.caseNumber.value),
          },
          rawCaseNumbers: action.payload.rawCaseNumbers,
          jointCaseNumbers: state.formData.jointCaseNumbers?.map((item) => ({
            id: item.id,
            textfieldValue: formatCaseNumber(item.textfieldValue),
            value: formatCaseNumber(item.value),
            debtorName: item.debtorName ?? '',
          })),
          multipleCaseNumbersToChoose: requiredToChooseCaseNumber
            ? mapMultipleCaseNumbers(
                state.formData.jointCaseNumbers.map((item) => ({
                  ...item,
                  textfieldValue: formatCaseNumber(item.textfieldValue),
                  value: formatCaseNumber(item.value),
                })),
                action.payload.multipleCaseOptionsList,
              )
            : state.formData.multipleCaseNumbersToChoose,
          caseData: {
            debtorName: action.payload.caseInfo.debtorName,
            jointDebtorName: action.payload.caseInfo.jointDebtorName,
          },
          caseChapter: action.payload.caseInfo.chapter,
          creditors: formatCreditors(action.payload),
          caseParties: state.formData.caseSource.importCaseParties ? formatParties(action.payload) : [],
          judgeName: action.payload.caseInfo.judge ?? '',
        };

        state.hasFetchedCase = true;
        state.fetchingOnlyCaseParties = false;
        state.fetchingOnlyCreditors = false;
      })
      .addCase(fetchCaseInfo.rejected, (state, action) => {
        // TODO: handle error
        const { message, clientCode } = action.payload as { message: string; clientCode: string };
        state.hasFetchedCase = false;
        state.fetchingOnlyCaseParties = false;
        state.caseInfoState = {
          message,
          status: 'error',
        };

        if (clientCode === 'PACER_UNAVAILABLE') {
          state.formData = {
            ...state.formData,
            isECFUnavailable: true,
            caseSource: {
              userSupplied: true,
              importCaseParties: false,
              masterMailingMatrix: false,
            },
          };
        }
        // state.error = action.error.message;
      })
      .addCase(sendDataForProcessing.fulfilled, (state, action) => {
        const { debtorName, trackingNumber, caseNumbers } = action.payload.data;

        const IP = action.payload.headers['x-client-ip'];

        state.isCreatingNotice = { status: 'success' };
        state.jobCreationResponse = {
          debtorName,
          trackingNumber,
          caseNumbers,
          ipAddress: IP,
        };
        state.submitJobError = '';
      })
      .addCase(sendDataForProcessing.rejected, (state, action: any) => {
        state.submitJobError = 'Failed to submit job. Please try again';
        for (let err of action.payload) {
          if (err.includes('address should not be empty')) {
            state.submitJobError = 'Failed to submit job. Some recipients have empty address';
          }
          if (err.includes('Failed payments')) {
            state.submitJobError = 'Please resolve failed payments to submit job';
          }
        }
        state.isCreatingNotice = { status: 'error' };
      })
      .addCase(sendDataForProcessing.pending, (state) => {
        state.submitJobError = '';
      })
      .addCase(addReturnAddressToDb.pending, (state) => {
        state.returnAddrStatus = { status: 'pending', message: '' };
      })
      .addCase(addReturnAddressToDb.fulfilled, (state, action) => {
        state.formData = {
          ...state.formData,
          returnAddresses: [
            ...state.formData.returnAddresses.map((address) => ({
              ...address,
              selected: false,
            })),
            { ...action.payload },
          ],
        };

        state.returnAddrStatus = {
          status: 'success',
          message: 'Successfully created return address.',
        };
      })
      .addCase(addReturnAddressToDb.rejected, (state) => {
        state.returnAddrStatus = {
          status: 'error',
          message: 'Error creating new address. Please try again later!',
        };
      })

      .addCase(getReturnAddresses.pending, (state) => {})

      .addCase(getReturnAddresses.fulfilled, (state, action) => {
        state.formData = {
          ...state.formData,
          returnAddresses: action.payload.map((item: ReturnAddress) => ({
            ...item,
            selected: item.isDefault || item.selected || false,
          })),
        };

        state.hasFechedReturnAddresses = true;
      })
      .addCase(editReturnAddressInDb.pending, (state) => {
        state.returnAddrStatus = { status: 'pending', message: '' };
      })
      .addCase(editReturnAddressInDb.fulfilled, (state, action) => {
        state.formData = {
          ...state.formData,
          returnAddresses: state.formData.returnAddresses.map((item) =>
            item.id === action.payload.id ? { ...action.payload } : item,
          ),
        };

        state.returnAddrStatus = {
          status: 'success',
          message: 'Successfully updated address',
        };
      })
      .addCase(editReturnAddressInDb.rejected, (state) => {
        state.returnAddrStatus = {
          status: 'error',
          message: 'Error updating address',
        };
      })
      .addCase(getUserCertificates.pending, (state) => {})
      .addCase(getUserCertificates.fulfilled, (state, action) => {
        state.formData = {
          ...state.formData,
          signatureBlocks: action.payload.map((item: SignatureBlock) => ({
            ...item,
            selected: item.isDefault || item.selected || false,
          })),
        };

        state.hasFetchedSignatures = true;
      })
      .addCase(removeReturnAddressInDb.pending, (state) => {
        state.returnAddrStatus = { status: 'pending', message: '' };
      })
      .addCase(removeReturnAddressInDb.fulfilled, (state, action) => {
        state.formData = {
          ...state.formData,
          returnAddresses: state.formData.returnAddresses
            .filter((item) => item.id !== action.payload)
            .map((item) => ({
              ...item,
              selected: item.isDefault ? true : false,
            })),
        };

        state.returnAddrStatus = {
          status: 'success',
          message: 'Successfully deleted return address.',
        };
      })
      .addCase(removeReturnAddressInDb.rejected, (state) => {
        state.returnAddrStatus = {
          status: 'error',
          message: 'An error occured deleting the return address. Please try again later.',
        };
      })
      .addCase(addCertificate.pending, (state) => {
        state.certificateStatus = { status: 'pending', message: '' };
      })
      .addCase(addCertificate.fulfilled, (state, action) => {
        state.formData = {
          ...state.formData,
          signatureBlocks: [
            ...state.formData.signatureBlocks.map((address) => ({
              ...address,
              selected: false, // Deselect all existing addresses
            })),
            { ...action.payload, selected: true }, // Add the new address with selected: true
          ],
        };

        state.certificateStatus = {
          status: 'success',
          message: 'Successfully added certificate',
        };
      })
      .addCase(editCertificatesInDB.pending, (state) => {
        state.certificateStatus = { status: 'pending', message: '' };
      })
      .addCase(editCertificatesInDB.fulfilled, (state, action) => {
        state.formData = {
          ...state.formData,
          signatureBlocks: state.formData.signatureBlocks.map((item) =>
            item.id === action.payload.id ? { ...action.payload } : item,
          ),
        };

        state.certificateStatus = {
          status: 'success',
          message: 'Successfully edited signature',
        };
      })
      .addCase(editCertificatesInDB.rejected, (state) => {
        state.certificateStatus = {
          status: 'error',
          message: 'An error occured editing your signature. Please try again later.',
        };
      })
      .addCase(removeCertificate.pending, (state) => {
        state.certificateStatus = { status: 'pending', message: '' };
      })
      .addCase(removeCertificate.fulfilled, (state, action) => {
        state.formData = {
          ...state.formData,
          signatureBlocks: state.formData.signatureBlocks
            .filter((item) => item.id !== action.payload)
            .map((item) => ({
              ...item,
              selected: item.isDefault ? true : false,
            })),
        };

        state.certificateStatus = {
          status: 'success',
          message: 'Successfully removed signature',
        };
      })
      .addCase(removeCertificate.rejected, (state) => {
        state.certificateStatus = {
          status: 'error',
          message: 'An error occured removing your signature. Please try again',
        };
      })
      .addCase(processUserSuppliedAddress.fulfilled, (state, action) => {
        state.formData = {
          ...state.formData,
          isLoadingUploadedAddresses: false,
          userUploadedAddresses: [...state.formData.userUploadedAddresses, ...action.payload],
          userUploadedAddressFiles: state.formData.userUploadedAddressFiles + 1,
        };
        state.uploadAddressesError = '';
      })
      .addCase(processUserSuppliedAddress.rejected, (state, action) => {
        const raw = action.payload as string;

        // PDF errors that should be surfaced to the user as a prompt to
        // reformat their address list using the Address Formatting Tool.
        const PDF_ADDR_TOOL_URL =
          'https://certificateofservice.com/address-formatting-tool-legal-pros';

        const PDF_ADDR_TOOL_MESSAGE =
          'Your PDF could not be processed. Please reformat your address list ' +
          'using our Address Formatting Tool: ' +
          PDF_ADDR_TOOL_URL;

        const PDF_PAGE_LIMIT_MESSAGE =
          'This PDF exceeds the page limit for automatic processing. ' +
          'Please reduce the number of pages or reformat your address list ' +
          'using our Address Formatting Tool: ' +
          PDF_ADDR_TOOL_URL;

        // Substrings that identify PDF errors where the formatting tool is the
        // right next step for the user.
        const pdfFormattingErrorSignatures = [
          'does not contain selectable text',           // scanned / image PDF
          'no address content was found after OCR',     // OCR produced nothing
          'No addresses could be found in this PDF',    // parsing produced nothing
          'No addresses could be extracted from this PDF', // zero addresses
          'File is corrupted',                          // corrupted file
          'PDF page rendering failed during OCR',       // OCR render failure
        ];

        if (raw === 'Invalid headers') {
          state.uploadAddressesError =
            'Invalid addresses table headers, please follow template';
        } else if (raw === 'Internal Server Error') {
          state.uploadAddressesError = 'Something went wrong';
        } else if (
          pdfFormattingErrorSignatures.some((sig) => raw?.includes(sig))
        ) {
          state.uploadAddressesError = PDF_ADDR_TOOL_MESSAGE;
        } else if (raw?.includes('Automatic text recognition is limited to')) {
          // Page-limit error: offer both reducing pages and the formatting tool.
          state.uploadAddressesError = PDF_PAGE_LIMIT_MESSAGE;
        } else {
          state.uploadAddressesError = raw;
        }

        state.formData.isLoadingUploadedAddresses = false;
      })
      .addCase(processUserSuppliedAddress.pending, (state, action) => {
        state.uploadAddressesError = '';
        state.formData.isLoadingUploadedAddresses = true;
      })
      .addCase(checkAvailableServiceOptions.fulfilled, (state, action) => {
        state.availableServiceOptions = action.payload;
      });
  },
});

export const {
  setNoticeFlowType,
  updateFormData,
  addSelectedCourtDistrict,
  addUploadedPdfs,
  addUploadedProofOfClaim,
  addUploadedBallot,
  updateServedParties,
  removeUploadedPdf,
  removeUploadedProofOfClaimPdf,
  removeUploadedBallotPdf,
  updateUploadedPfs,
  accumulateTotalPages,
  setFocusedFileInStore,
  updateCasePartyStatus,
  updateCreditorStatus,
  updateAttorneyStatus,
  updateUserAddedStatus,
  updateHearingInformation,
  updateCustomFileName,
  addCaseDocketIds,
  updateDebtor,
  updateNewJersey,
  removeCaseDocketIds,
  removeReturnAddress,
  loadUserSettings,
  updateCaseDocketEntriesMetaData,
  cancelCreation,
  setErrors,
  setReturnErrors,
  setSignatureErrors,
  removeSignatureErrors,
  setClerkErrors,
  startNoticeCreation,
  clearErrors,
  resetChapterOptions,
  removeReturnErrors,
  updateReturnEnvelope,
  resetReturnAddrStatus,
  resetCertificateStatus,
  resetCaseStatus,
  reorderUploadedPdfs,
  updateCourtJurisdiction,
  updateLegalCaseFields,
  resetErrors,
  setCaseInfoError,
  clearUploadAddressesError,
  addJointCaseNumber,
  deleteJoinCaseNumber,
  updateJointCaseNumber,
  updateJointCaseNumberDebtorName,
  addPdfSizeToTotal,
  clearSubmitJobError,
  setOnlyFetchingCaseParties,
  setOnlyFetchingCreditors,
  setCasePartiesToCmEcfMailing,
  revertCasePartiesFromCmEcfToPreviousState,
  setRecipientAddressCountry,
  setRecipientUserApproved,
  setPendingEditAddressId,
} = uploadSlice.actions;

/**
 * Classify a single recipient's address by calling POST /job/classify-addresses.
 * Defined after uploadSlice so it can reference uploadSlice.actions directly.
 * Also extracts isValidAddress from the response for user-uploaded addresses.
 *
 * Usage:
 *   dispatch(classifyRecipientAddress({ recipientType: 'caseParty', id: recipient.id, address: recipient.address }))
 */
export const classifyRecipientAddress = createAsyncThunk(
  'upload/classifyRecipientAddress',
  async (
    payload: { recipientType: 'caseParty' | 'creditor' | 'userUploaded'; id: string; address: string[] },
    { dispatch },
  ) => {
    try {
      const { data } = await apiInstance.post('/job/classify-addresses', {
        addresses: [payload.address],
      });
      const country = data?.results?.[0]?.country as string | undefined;
      const normalizedLines = data?.results?.[0]?.normalizedLines as string[] | undefined;
      // Only propagate isValidAddress for user-uploaded addresses (PACER sources are trusted).
      const isValidAddress = payload.recipientType === 'userUploaded'
        ? (data?.results?.[0]?.isValidAddress as boolean | undefined) ?? true
        : undefined;
      dispatch(
        uploadSlice.actions.setRecipientAddressCountry({
          recipientType: payload.recipientType,
          id: payload.id,
          country,
          normalizedLines,
          isValidAddress,
        }),
      );
    } catch {
      // Classification failure is non-fatal — addressCountry/isValidAddress remain unchanged,
      // fallback classification runs server-side at job submission.
    }
  },
);

export default uploadSlice.reducer;
